import { DisablerRequester } from '@cobliteam/api-lib';
import type { VehicleDriverAuthorization, VehicleDto } from '@cobliteam/api-lib/src/__generated__/graphql';
import GrahpQLVehicleRequester from '@cobliteam/api-lib/src/dashboard/vehicle/GrahpQLVehicleRequester';
import { DisablerAction, type DisablerEvent, DisablerStatus, type Vehicle, type VehicleDriverAuthorizationMode } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { type UseQueryOptions, type UseSuspenseQueryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { QUERY_KEY as DEVICES_QUERY_KEY } from '../useDevices';

interface ImmobileActionProps {
  deviceId: string;
  disablerAction: DisablerAction;
}

// Lazy instantiation to avoid circular dependency issues
const getVehicleGraphQLRequester = () => new GrahpQLVehicleRequester();

export const QUERY_KEY = 'VEHICLES';

const env = Environment.getInstance().getEnvironment();

interface UseVehicle {
  withOdometer?: boolean;
  withDisablerStatus?: boolean;
  withDriverAuthorizations?: boolean;
  withImmobilizerStatus?: boolean;
  withConsumptionGoal?: boolean;
}

const useVehicle = ({ withOdometer = false, withDisablerStatus = true, withImmobilizerStatus = false, withConsumptionGoal = false }: UseVehicle = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('vehicles');

  const getVehicles = ({ refetchOnMount = false, ...options }: Partial<UseQueryOptions<Vehicle[]>>, vehicleGroupsIds?: string[]) => {
    let QUERY_KEYS = [QUERY_KEY];
    if (vehicleGroupsIds && vehicleGroupsIds.length > 0) QUERY_KEYS = [...QUERY_KEYS, ...vehicleGroupsIds];
    if (withOdometer) QUERY_KEYS.push('ODOMETER');
    if (withDisablerStatus) QUERY_KEYS.push('DISABLER_STATUS');
    if (withImmobilizerStatus) QUERY_KEYS.push('IMMOBILIZER_STATUS');
    if (withConsumptionGoal) QUERY_KEYS.push('CONSUMPTION_GOAL');

    return useQuery<Vehicle[]>({
      queryKey: QUERY_KEYS,
      queryFn: () => getVehicleGraphQLRequester().getVehicles(withOdometer, withDisablerStatus, vehicleGroupsIds, withImmobilizerStatus, withConsumptionGoal),
      refetchOnMount,
      ...options,
    });
  };

  const getVehiclesSuspense = ({ refetchOnMount = false, ...options }: Partial<UseSuspenseQueryOptions<Vehicle[]>> = {}, vehicleGroupsIds?: string[]) => {
    let QUERY_KEYS = [QUERY_KEY];
    if (vehicleGroupsIds && vehicleGroupsIds.length > 0) QUERY_KEYS = [...QUERY_KEYS, ...vehicleGroupsIds];
    if (withOdometer) QUERY_KEYS.push('ODOMETER');
    if (withDisablerStatus) QUERY_KEYS.push('DISABLER_STATUS');
    if (withImmobilizerStatus) QUERY_KEYS.push('IMMOBILIZER_STATUS');

    return useSuspenseQuery<Vehicle[]>({
      queryKey: QUERY_KEYS,
      queryFn: () => getVehicleGraphQLRequester().getVehicles(withOdometer, withDisablerStatus, vehicleGroupsIds, withImmobilizerStatus),
      refetchOnMount,
      ...options,
    });
  };

  const getVehicle = (vehicleId: string, { enabled = true, refetchOnMount = false, ...options }: Partial<UseQueryOptions<Vehicle>>) => {
    const QUERY_KEYS = [QUERY_KEY, vehicleId];
    if (withOdometer) QUERY_KEYS.push('ODOMETER');
    if (withDisablerStatus) QUERY_KEYS.push('DISABLER_STATUS');

    return useQuery<Vehicle>({
      ...options,
      queryKey: QUERY_KEYS,
      queryFn: () => getVehicleGraphQLRequester().getVehicle(vehicleId, withOdometer, withDisablerStatus),
      enabled,
      refetchOnMount,
    });
  };

  const useGetVehicleDriverAuthorizations = (vehicleId: string, { enabled = true, refetchOnMount = false, ...options }: Partial<UseQueryOptions<VehicleDriverAuthorization>>) => {
    const queryKey = [QUERY_KEY, vehicleId, 'VEHICLE_DRIVER_AUTHORIZATIONS'].filter(Boolean);
    return useQuery<VehicleDriverAuthorization>({
      ...options,
      queryKey,
      queryFn: () => getVehicleGraphQLRequester().getVehicleDriverAuthorization(vehicleId),
      enabled,
      refetchOnMount,
    });
  };

  const useUpdateVehicleDriverAuthorizations = useMutation<
    VehicleDto,
    unknown,
    { vehicleId: string; driverIds: string[]; isImmobilizerEnabled: boolean; vehicleDriverAuthorizationMode: VehicleDriverAuthorizationMode }
  >({
    mutationFn: async ({ vehicleId, driverIds, isImmobilizerEnabled, vehicleDriverAuthorizationMode }) =>
      getVehicleGraphQLRequester().updateVehicleDriverAuthorizations(vehicleId, driverIds, isImmobilizerEnabled, vehicleDriverAuthorizationMode),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'DRIVER_AUTHORIZATIONS'],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'VEHICLE_DRIVER_AUTHORIZATIONS'],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'DISABLER_STATUS'],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'IMMOBILIZER_STATUS'],
      });
      Alert.success('Trava editada com sucesso!');
    },
    onError: () => {
      Alert.error('Erro ao editar trava!');
    },
  });

  const useValidateImportVehicleSpreadsheet = useMutation({
    mutationFn: getVehicleGraphQLRequester().validateImportVehicleSpreadsheet,
    onSuccess: () => {
      Alert.success(t('sheet_import.validate_success'));
    },
    onError: () => {
      Alert.error(t('sheet_import.validate_error'));
    },
  });

  const useVehicleSuggestionsBatch = useMutation({
    mutationFn: (plates: string[]) => getVehicleGraphQLRequester().getVehicleSuggestionsBatch(plates),
    onSuccess: () => {
      Alert.success(t('sheet_import.suggestions_success'));
    },
    onError: () => {
      Alert.error(t('sheet_import.suggestions_error'));
    },
  });

  const useCreateVehiclesBatch = useMutation({
    mutationFn: getVehicleGraphQLRequester().createVehiclesBatch,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY],
      });
      Alert.success(t('sheet_import.import_success'));
    },
    onError: () => {
      Alert.error(t('sheet_import.import_error'));
    },
  });

  return {
    useUpdateVehicleDriverAuthorizations,
    useGetVehicleDriverAuthorizations,
    getVehicles,
    getVehiclesSuspense,
    getVehicle,
    useValidateImportVehicleSpreadsheet,
    useVehicleSuggestionsBatch,
    useCreateVehiclesBatch,
  };
};

const getRequesterMethod = (action: DisablerAction) => {
  const disablerRequester = new DisablerRequester({ host: env.herbieApiHost });
  if (action === DisablerAction.Block || action === DisablerAction.CancelUnblock) {
    return disablerRequester.requestDisable;
  }
  return disablerRequester.requestEnable;
};

const immobilizerAction = async ({ deviceId, disablerAction }: ImmobileActionProps) => {
  const requester = getRequesterMethod(disablerAction);
  await requester(deviceId);
};

export const useImmobilizerAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: immobilizerAction,
    onSuccess: (_, { deviceId, disablerAction }) => {
      queryClient.setQueryData<Vehicle[]>([QUERY_KEY], (vehicles) => {
        if (!vehicles) return vehicles;

        const vehicleIndex = vehicles.findIndex((vehicle) => vehicle.deviceId === deviceId);

        if (vehicleIndex !== -1) {
          const foundedVehicle = vehicles[vehicleIndex];
          if (foundedVehicle.disablerStatus) {
            const updateVehicle = {
              ...foundedVehicle,
              disablerStatus: {
                ...foundedVehicle.disablerStatus,
                lastDisablingActionTime: Date.now(),
                status: disablerAction === DisablerAction.Block || disablerAction === DisablerAction.CancelUnblock ? DisablerStatus.DisablePending : DisablerStatus.EnablePending,
              },
            };
            return [...vehicles.slice(0, vehicleIndex), updateVehicle, ...vehicles.slice(vehicleIndex + 1)];
          }
        }

        return vehicles;
      });

      queryClient.setQueryData<DisablerEvent[]>([DEVICES_QUERY_KEY, 'FLEET_STATUS'], (events) => {
        if (!events) return events;

        const eventIndex = events.findIndex((e) => e.deviceId === deviceId);

        if (eventIndex !== -1) {
          const foundedEvent = events[eventIndex];

          const updateEvent = {
            ...foundedEvent,
            lastDisablingActionTime: Date.now(),
            status: disablerAction === DisablerAction.Block || disablerAction === DisablerAction.CancelUnblock ? DisablerStatus.DisablePending : DisablerStatus.EnablePending,
          };

          return [...events.slice(0, eventIndex), updateEvent, ...events.slice(eventIndex + 1)];
        }

        return events;
      });

      Alert.warning(
        disablerAction === DisablerAction.Block || disablerAction === DisablerAction.CancelUnblock
          ? 'O bloqueio deste veículo já está em andamento!'
          : 'O desbloqueio deste veículo já está em andamento!'
      );
    },
    onError: (_, { disablerAction }) => {
      Alert.error(
        disablerAction === DisablerAction.Block || disablerAction === DisablerAction.CancelUnblock
          ? 'Ops! O sistema apresentou um erro e não foi possível iniciar o bloqueio, tente novamente.'
          : 'Ops! O sistema apresentou um erro e não foi possível iniciar o desbloqueio, tente novamente.'
      );
    },
  });
};

export default useVehicle;
